/**
 * Smart PDF Processor Service - Servicio inteligente para procesar PDFs con OpenAI
 * Extrae texto de PDFs y los procesa con GPT para obtener transacciones financieras
 */

import * as pdfjsLib from 'pdfjs-dist';
import { OpenAIService } from './openai.service';
import { LoggerService } from './logger.service';

// Configurar PDF.js worker - configuración optimizada para navegador
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

export interface SmartPDFProcessingResult {
  success: boolean;
  data?: {
    ingresos: Array<{
      concepto: string;
      monto: number;
      categoria: string;
      fecha: Date;
      descripcion?: string;
    }>;
    egresos: Array<{
      concepto: string;
      monto: number;
      categoria: string;
      fecha: Date;
      descripcion?: string;
    }>;
    resumen: {
      totalIngresos: number;
      totalEgresos: number;
      transaccionesTotales: number;
      fechaInicio: string;
      fechaFin: string;
    };
  };
  error?: string;
  metadata?: {
    fileName: string;
    fileSize: number;
    pageCount: number;
    textLength: number;
    processingTime: number;
  };
}

export class SmartPDFProcessorService {
  private openAIService: OpenAIService;

  constructor() {
    this.openAIService = new OpenAIService();
  }

  /**
   * Verifica si el servicio está configurado correctamente
   */
  isConfigured(): boolean {
    return this.openAIService.isConfigured();
  }

  /**
   * Procesa un archivo PDF completo con inteligencia artificial
   */
  async processPDF(file: File): Promise<SmartPDFProcessingResult> {
    const startTime = Date.now();
    
    LoggerService.info('Starting smart PDF processing', 'SmartPDFProcessorService', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    try {
      // Validar archivo
      this.validatePDFFile(file);

      // Extraer texto del PDF
      const { text: extractedText, pageCount } = await this.extractTextFromPDF(file);
      
      if (!extractedText.trim()) {
        throw new Error('No se pudo extraer texto del PDF. El archivo puede estar vacío o contener solo imágenes.');
      }

      // Procesar con OpenAI
      const processedData = await this.openAIService.processBankDocument(extractedText);

      // Convertir fechas de string a Date
      const result: SmartPDFProcessingResult = {
        success: true,
        data: {
          ingresos: processedData.ingresos.map(i => ({
            ...i,
            fecha: new Date(i.fecha)
          })),
          egresos: processedData.egresos.map(e => ({
            ...e,
            fecha: new Date(e.fecha)
          })),
          resumen: processedData.resumen
        },
        metadata: {
          fileName: file.name,
          fileSize: file.size,
          pageCount,
          textLength: extractedText.length,
          processingTime: Date.now() - startTime
        }
      };

      LoggerService.logUserAction('smart_pdf_processed_successfully', 'SmartPDFProcessorService', {
        fileName: file.name,
        ingresos: result.data?.ingresos.length || 0,
        egresos: result.data?.egresos.length || 0,
        pageCount,
        processingTime: result.metadata?.processingTime
      });

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido procesando PDF';
      
      LoggerService.error('Smart PDF processing failed', 'SmartPDFProcessorService', {
        fileName: file.name,
        error: errorMessage,
        processingTime: Date.now() - startTime
      });

      return {
        success: false,
        error: errorMessage,
        metadata: {
          fileName: file.name,
          fileSize: file.size,
          pageCount: 0,
          textLength: 0,
          processingTime: Date.now() - startTime
        }
      };
    }
  }

  /**
   * Valida que el archivo sea un PDF válido
   */
  private validatePDFFile(file: File): void {
    // Validar tipo de archivo
    if (file.type !== 'application/pdf') {
      throw new Error('El archivo debe ser un PDF válido');
    }

    // Validar tamaño (máximo 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('El archivo PDF es demasiado grande. Tamaño máximo: 10MB');
    }

    // Validar que no esté vacío
    if (file.size === 0) {
      throw new Error('El archivo PDF está vacío');
    }
  }

  /**
   * Extrae texto de un archivo PDF usando PDF.js
   */
  private async extractTextFromPDF(file: File): Promise<{ text: string; pageCount: number }> {
    try {
      LoggerService.info('Extracting text from PDF', 'SmartPDFProcessorService', {
        fileName: file.name
      });

      // Leer el archivo como ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Cargar el documento PDF
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      LoggerService.info('PDF loaded successfully', 'SmartPDFProcessorService', {
        pageCount: pdf.numPages
      });

      let fullText = '';

      // Extraer texto de cada página
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          
          // Combinar todos los elementos de texto
          const pageText = textContent.items
            .map((item: unknown) => {
              const textItem = item as { str?: string };
              if (textItem && 'str' in textItem) {
                return textItem.str || '';
              }
              return '';
            })
            .join(' ');

          fullText += pageText + '\n\n';

          LoggerService.info(`Extracted text from page ${pageNum}`, 'SmartPDFProcessorService', {
            pageNum,
            textLength: pageText.length
          });

        } catch (pageError) {
          LoggerService.warn(`Failed to extract text from page ${pageNum}`, 'SmartPDFProcessorService', {
            pageNum,
            error: pageError
          });
          // Continuar con las otras páginas
        }
      }

      LoggerService.info('Text extraction completed', 'SmartPDFProcessorService', {
        totalPages: pdf.numPages,
        totalTextLength: fullText.length
      });

      return {
        text: fullText.trim(),
        pageCount: pdf.numPages
      };

    } catch (error) {
      LoggerService.error('PDF text extraction failed', 'SmartPDFProcessorService', { error });
      
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      
      // Detectar errores específicos del worker
      if (errorMessage.includes('worker') || errorMessage.includes('fetch dynamically imported module')) {
        throw new Error('Error de configuración de PDF.js. Intenta refrescar la página o contacta soporte técnico.');
      }
      
      if (errorMessage.includes('Invalid PDF')) {
        throw new Error('El archivo no es un PDF válido o está dañado.');
      }
      
      throw new Error('Error al extraer texto del PDF: ' + errorMessage);
    }
  }

  /**
   * Obtiene un resumen del texto extraído (para preview)
   */
  async getTextPreview(file: File, maxLength: number = 500): Promise<string> {
    try {
      const { text: fullText } = await this.extractTextFromPDF(file);
      return fullText.length > maxLength 
        ? fullText.substring(0, maxLength) + '...'
        : fullText;
    } catch (error) {
      LoggerService.error('Failed to get PDF text preview', 'SmartPDFProcessorService', { error });
      return 'Error al obtener vista previa del texto';
    }
  }

  /**
   * Procesa múltiples archivos PDF
   */
  async processMultiplePDFs(files: File[]): Promise<SmartPDFProcessingResult[]> {
    LoggerService.info('Processing multiple PDFs', 'SmartPDFProcessorService', {
      fileCount: files.length
    });

    const results: SmartPDFProcessingResult[] = [];

    for (const file of files) {
      try {
        const result = await this.processPDF(file);
        results.push(result);
      } catch (error) {
        results.push({
          success: false,
          error: error instanceof Error ? error.message : 'Error desconocido',
          metadata: {
            fileName: file.name,
            fileSize: file.size,
            pageCount: 0,
            textLength: 0,
            processingTime: 0
          }
        });
      }
    }

    LoggerService.logUserAction('multiple_smart_pdfs_processed', 'SmartPDFProcessorService', {
      fileCount: files.length,
      successCount: results.filter(r => r.success).length,
      errorCount: results.filter(r => !r.success).length
    });

    return results;
  }
}